import { useRef } from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Star } from 'lucide-react';
import type { AppDefinition, AppStatus } from '../types';

const STATUS_UI: Record<
  AppStatus,
  { dot: string; text: string; label: string }
> = {
  prod: {
    dot: 'bg-emerald-600 dark:bg-emerald-400',
    text: 'text-emerald-600 dark:text-emerald-400',
    label: 'Prod',
  },
  integration: {
    dot: 'bg-amber-500 dark:bg-amber-400',
    text: 'text-amber-500 dark:text-amber-400',
    label: 'Beta',
  },
  dev: {
    dot: 'bg-slate-400 dark:bg-slate-500',
    text: 'text-slate-400 dark:text-slate-500',
    label: 'Dev',
  },
  poc: {
    dot: 'bg-violet-500 dark:bg-violet-400',
    text: 'text-violet-500 dark:text-violet-400',
    label: 'PoC',
  },
  test: {
    dot: 'bg-blue-500 dark:bg-blue-400',
    text: 'text-blue-500 dark:text-blue-400',
    label: 'Test',
  },
};

function DynamicIcon({
  name,
  className,
  style,
  size,
}: {
  name: string;
  className?: string;
  style?: React.CSSProperties;
  size?: number;
}) {
  const Cmp = (LucideIcons as unknown as Record<string, LucideIcon>)[name];
  if (!Cmp) {
    const Fallback = LucideIcons.Box;
    return <Fallback className={className} style={style} size={size} />;
  }
  return <Cmp className={className} style={style} size={size} />;
}

export interface AppCardProps {
  app: AppDefinition;
  showStar: boolean;
  isFav: boolean;
  onToggleFav: () => void;
  /** Index pour délai d'animation en cascade */
  index?: number;
  /** Email du compte Hub → passé en `gs_hint` pour le SSO silencieux des apps */
  userEmail?: string;
}

function buildAppOpenUrl(baseUrl: string, userEmail?: string): string {
  const email = userEmail?.trim();
  if (!email) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}gs_hint=${encodeURIComponent(email)}`;
}

export function AppCard({ app, showStar, isFav, onToggleFav, index = 0, userEmail }: AppCardProps) {
  const status = STATUS_UI[app.status];
  const hasUrl = Boolean(app.url?.trim());
  const showFavorite = showStar && hasUrl;
  const showAdminBadge = Boolean(app.adminOnly && hasUrl);
  const displayName = app.name.replace(/^GS-/, '');

  const openApp = () => {
    if (!hasUrl || !app.url) return;
    const url = buildAppOpenUrl(app.url.trim(), userEmail);
    window.open(url, '_blank', 'noopener');
  };

  const interactive = hasUrl;

  const pinged = useRef(false);

  const handleMouseEnter = () => {
    if (!hasUrl || pinged.current || !app.url) return;
    pinged.current = true;
    fetch(app.url.trim(), { method: 'HEAD', mode: 'no-cors', cache: 'no-store' }).catch(() => {});
  };

  return (
    <article
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onMouseEnter={interactive ? handleMouseEnter : undefined}
      onClick={interactive ? openApp : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openApp();
              }
            }
          : undefined
      }
      className={`gs-card-enter group relative flex w-full flex-col items-center rounded-xl border border-slate-200 bg-white px-2 py-3 shadow-sm transition-all duration-150 select-none dark:border-slate-700 dark:bg-slate-800 ${
        interactive
          ? 'cursor-pointer hover:scale-105 active:scale-95 hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600'
          : 'cursor-not-allowed pointer-events-none'
      }`}
      style={{
        animationDelay: `${index * 25}ms`,
        ...(interactive ? {} : { opacity: 0.4 }),
      }}
    >
      {showFavorite ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFav();
          }}
          // Si le ruban Admin est présent (~16px de haut), descendre l'étoile sous le ruban
          className={`absolute right-1.5 z-20 rounded p-0.5 opacity-40 transition-opacity duration-150 group-hover:opacity-100 ${
            showAdminBadge ? 'top-5' : 'top-1.5'
          }`}
          title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          aria-pressed={isFav}
        >
          <Star
            size={12}
            className={
              isFav
                ? 'fill-amber-400 text-amber-400 dark:fill-amber-400 dark:text-amber-400'
                : 'fill-slate-200 text-slate-400 dark:fill-slate-600 dark:text-slate-500'
            }
            strokeWidth={2}
          />
        </button>
      ) : null}

      {showAdminBadge ? (
        <span className="absolute inset-x-0 top-0 z-10 flex items-center justify-center rounded-t-xl bg-slate-800/90 py-0.5 text-chip font-semibold uppercase tracking-wide text-white dark:bg-slate-200/90 dark:text-slate-900">
          Admin
        </span>
      ) : null}

      <div
        className="mx-auto mb-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: app.iconBg }}
      >
        <DynamicIcon name={app.icon} size={18} style={{ color: app.iconColor }} />
      </div>

      <span className="w-full truncate text-center text-meta font-semibold leading-tight text-slate-800 dark:text-slate-100">
        {displayName}
      </span>

      {app.desc ? (
        <span className="mt-0.5 line-clamp-2 w-full text-center text-chip leading-tight text-slate-400 dark:text-slate-500">
          {app.desc}
        </span>
      ) : null}

      <div className="mt-1.5 flex items-center gap-1">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${status.dot}`} aria-hidden />
        <span className={`text-chip ${status.text}`}>{status.label}</span>
      </div>
    </article>
  );
}
